import app from 'flarum/forum/app';
import Icon from 'flarum/common/components/Icon';
import Component from 'flarum/common/Component';
import LivePanel from './LivePanel';
const T = function (key, p) {
  if (p === void 0) {
    p = {};
  }
  return app.translator.trans(`linkrobins-lr-monitoring.forum.landing.${key}`, p);
};

/**
 * The uptime monitoring section of the home page: a heading, a live panel in
 * front of it (real response times from the real checker, not a mock), six
 * reasons each carrying a sliver of the product, and the one price — $0.
 *
 * The copy promises only what this engine does: HTTP, keyword, TCP and
 * heartbeat checks; SSL and domain expiry; email alerts and per-monitor
 * webhooks. Nothing about thirteen alert channels.
 *
 * This was the whole of /uptime until 2026-09-27, when the lander was merged
 * into the home page. The step-by-step, the FAQ and the closing CTA went to
 * the wiki; the hero went to the home page's own. /uptime now 301s to
 * /#uptime, which is the id on the section below.
 *
 * 🛑 A Component, NOT a Page — and it must stay one.
 *
 * Page scrolls to the top on mount, registers with app.current and sets the
 * body class and title, all of which a standalone lander wants and a section
 * of someone else's page must not do. A Page inside a Page is what took the
 * home page down on the day of the merge: HomePage, UptimeLanding and
 * ConsultingPage each ran that lifecycle and the body rendered empty. The
 * build was clean and CI was green — only a render caught it.
 */
export default class UptimeLanding extends Component {
  view() {
    return m("div", {
      className: "LrLander LrUptimeLander LrLander--embedded"
    }, this.intro(), this.reasons());
  }

  /**
   * The opener: a section heading in the home page's own idiom, then the live
   * panel. The panel is the best thing here — real checks, updating — so it
   * survived the merge even though the hero around it did not.
   */
  intro() {
    return m("section", {
      className: "LrLander-section is-shaded",
      id: "uptime"
    }, m("div", {
      className: "container"
    }, m("div", {
      className: "LrLander-head"
    }, m("span", {
      className: "LrHome-pill"
    }, T('eyebrow')), m("h2", {
      className: "LrLander-sectionTitle"
    }, T('title'), " ", m("span", {
      className: "LrLander-titleAccent"
    }, T('title_accent'))), m("p", {
      className: "LrLander-sectionLede"
    }, T('lede'))), m(LivePanel, null)));
  }

  /**
   * The Flarum checks first (they are what no general tool has), then the
   * six everyday reasons, each opening with a sliver of the product itself.
   * One section since 2026-10-12: "more than uptime" and "understands
   * Flarum" used to be two, and made the same point twice.
   */
  reasons() {
    const cards = [['ping', 'fas fa-heart-pulse', () => this.pingMock()], ['status', 'fas fa-square-poll-vertical', () => this.statusMock()], ['minute', 'fas fa-stopwatch', () => this.minuteMock()], ['alerts', 'fas fa-bell', () => this.alertsMock()], ['confirmed', 'fas fa-circle-check', () => this.confirmedMock()], ['visual', 'fas fa-camera', () => this.visualMock()]];
    return m("section", {
      className: "LrLander-section"
    }, m("div", {
      className: "container"
    }, m("h2", {
      className: "LrLander-sectionTitle"
    }, T('flarum.title')), m("p", {
      className: "LrLander-sectionLede"
    }, T('flarum.lede')), this.flarum(), m("div", {
      className: "LrLander-grid LrLander-grid--afterPitch"
    }, cards.map(_ref => {
      let [k, icon, mock] = _ref;
      return m("div", {
        className: "LrLander-card LrLander-card--mock",
        key: k
      }, m("div", {
        className: "LrLander-cardHead"
      }, m("span", {
        className: "LrLander-cardIcon LrLander-cardIcon--inline"
      }, m(Icon, {
        name: icon
      })), m("h3", {
        className: "LrLander-cardTitle"
      }, T(`reasons.${k}.title`))), m("p", {
        className: "LrLander-cardText"
      }, T(`reasons.${k}.text`)), m("div", {
        className: "LrLander-cardMock"
      }, mock()));
    }))));
  }

  /**
   * What the Flarum monitor type does that a generic HTTP check cannot.
   *
   * Inside the reasons band rather than a band of its own: the home page
   * alternates shaded and plain sections end to end, and a sixth band would
   * put two of the same next to each other. It belongs to the uptime pitch
   * either way.
   *
   * 🛑 Every line is a finding FlarumInspector actually produces. The
   * cadences in the copy are real too — the header findings are recomputed on
   * every ping; for Pro the /api read (debug, vitals, and "API down" as an
   * outage) happens on every check too (MonitorChecker::checkFlarumApi), and
   * the file-exposure probe runs on lr:monitors:flarum every three hours.
   * Free forums get the full inspection once a day. Do not add a claim here
   * without a check behind it.
   */
  flarum() {
    return m("div", {
      className: "LrFlarumPitch"
    }, m("div", {
      className: "LrFlarumPitch-body"
    }, m("ul", {
      className: "LrFlarumPitch-list"
    }, [['api', 'fas fa-plug-circle-exclamation'], ['files', 'fas fa-file-shield'], ['debug', 'fas fa-bug'], ['headers', 'fas fa-shield-halved'], ['vitals', 'fas fa-chart-simple']].map(_ref2 => {
      let [k, icon] = _ref2;
      return m("li", {
        key: k,
        className: "LrFlarumPitch-item"
      }, m("span", {
        className: "LrFlarumPitch-icon"
      }, m(Icon, {
        name: icon
      })), m("span", {
        className: "LrFlarumPitch-copy"
      }, m("strong", {
        className: "LrFlarumPitch-itemTitle"
      }, T(`flarum.checks.${k}.title`)), m("span", {
        className: "LrFlarumPitch-itemText"
      }, T(`flarum.checks.${k}.text`)), m("span", {
        className: `LrFlarumPitch-when ${k === 'api' ? 'is-pro' : ''}`
      }, T(`flarum.checks.${k}.when`))));
    })), this.flarumMock()));
  }

  /**
   * The findings panel, in the shape the dashboard draws it. Example data:
   * each check with the one-line detail the dashboard gives, then the forum's
   * own numbers, so the panel carries as much as the list beside it.
   */
  flarumMock() {
    const rows = [['mock_api', 'ok'], ['mock_files', 'ok'], ['mock_debug', 'ok'], ['mock_frames', 'review'], ['mock_hsts', 'review']];
    const vitals = [['mock_discussions', '1,284'], ['mock_posts', '18,906'], ['mock_members', '2,431'], ['mock_tags', '12'], ['mock_last', String(T('flarum.mock_last_value'))], ['mock_signups', String(T('flarum.mock_signups_value'))]];
    return m("div", {
      className: "LrFlarumPitch-mock"
    }, m("div", {
      className: "LrFlarumPitch-mockHead"
    }, m("span", null, T('flarum.mock_title')), m("span", {
      className: "LrMock-when"
    }, T('flarum.mock_when'))), m("ul", {
      className: "LrFlarumPitch-findings"
    }, rows.map(_ref3 => {
      let [key, state] = _ref3;
      return m("li", {
        className: "LrFlarumPitch-finding",
        key: key
      }, m("span", {
        className: "LrFlarumPitch-findingText"
      }, m("span", {
        className: "LrFlarumPitch-findingLabel"
      }, T(`flarum.${key}`)), m("span", {
        className: "LrFlarumPitch-findingDetail"
      }, T(`flarum.${key}_detail`))), m("span", {
        className: `LrMock-badge ${state === 'ok' ? 'is-good' : 'is-warn'}`
      }, T(state === 'ok' ? 'flarum.state_ok' : 'flarum.state_review')));
    })), m("div", {
      className: "LrFlarumPitch-vitalsHead"
    }, T('flarum.mock_vitals')), m("dl", {
      className: "LrFlarumPitch-vitals"
    }, vitals.map(_ref4 => {
      let [key, value] = _ref4;
      return m("div", {
        className: "LrFlarumPitch-vital",
        key: key
      }, m("dt", null, T(`flarum.${key}`)), m("dd", null, value));
    })));
  }

  /** The dashboard's three figures plus the two expiries, in miniature. */
  pingMock() {
    return [m("div", {
      className: "LrMock-stats"
    }, [['Uptime', '30 days', '99.98%', 'is-good'], ['Response', '1,440 checks', '442 ms', ''], ['Incidents', '0 s downtime', '0', '']].map(_ref5 => {
      let [label, sub, value, cls] = _ref5;
      return m("div", {
        className: "LrMock-stat"
      }, m("span", {
        className: "LrMock-statLabel"
      }, label), m("span", {
        className: "LrMock-statSub"
      }, sub), m("strong", {
        className: `LrMock-statValue ${cls}`
      }, value));
    })), m("div", {
      className: "LrMock-rows"
    }, m("div", {
      className: "LrMock-row"
    }, m("span", null, "SSL certificate"), m("span", {
      className: "LrMock-badge is-good"
    }, "Valid \xB7 67 days")), m("div", {
      className: "LrMock-row"
    }, m("span", null, "Domain expiry"), m("span", {
      className: "LrMock-badge is-good"
    }, "247 days")), m("div", {
      className: "LrMock-row"
    }, m("span", null, "Security grade"), m("span", {
      className: "LrMock-badge is-good"
    }, "A")))];
  }

  /** A status page's component list, all green. */
  statusMock() {
    return m("div", {
      className: "LrMock-rows LrMock-rows--boxed"
    }, m("div", {
      className: "LrMock-row LrMock-row--head"
    }, m("span", null, "All systems operational"), m("span", {
      className: "LrMock-live"
    }, m("i", null), " Live")), ['Website', 'API', 'Database', 'Email delivery'].map(c => m("div", {
      className: "LrMock-row"
    }, m("span", null, c), m("span", {
      className: "LrMock-badge is-good"
    }, "Operational"))));
  }

  /** Thirty checks, one bad — the bar every monitor shows. */
  minuteMock() {
    return [m("div", {
      className: "LrMock-row"
    }, m("span", null, "example.com"), m("span", {
      className: "LrMock-pct"
    }, "99.8%")), m("div", {
      className: "LrMock-bar"
    }, Array.from({
      length: 30
    }, (_, i) => m("i", {
      className: i === 24 ? 'is-down' : ''
    })))];
  }

  /** Where an alert lands: the inbox, and whatever a webhook feeds. */
  alertsMock() {
    return m("div", {
      className: "LrMock-chips"
    }, [['fas fa-envelope', 'Email'], ['fas fa-bolt', 'Webhook'], ['fab fa-slack', 'Slack'], ['fab fa-discord', 'Discord'], ['fas fa-diagram-project', 'n8n']].map(_ref6 => {
      let [icon, label] = _ref6;
      return m("span", {
        className: "LrMock-chip"
      }, m(Icon, {
        name: icon
      }), " ", label);
    }));
  }

  /** A failure that a second look clears: the alert that never had to be sent. */
  confirmedMock() {
    return m("div", {
      className: "LrMock-rows LrMock-rows--boxed"
    }, [['Failure seen', 'Re-checking', 'is-warn'], ['Second opinion', 'Site is up', 'is-good'], ['Alert', 'None sent', 'is-good']].map(_ref7 => {
      let [label, state, cls] = _ref7;
      return m("div", {
        className: "LrMock-row"
      }, m("span", null, label), m("span", {
        className: `LrMock-badge ${cls}`
      }, state));
    }));
  }

  /** Change Detection: yesterday's page against today's. */
  visualMock() {
    return m("div", {
      className: "LrMock-rows LrMock-rows--boxed"
    }, m("div", {
      className: "LrMock-row LrMock-row--head"
    }, m("span", null, "Page looks different"), m("span", {
      className: "LrMock-badge is-warn"
    }, "Review")), [['Before', 'Yesterday'], ['After', 'Today']].map(_ref8 => {
      let [label, when] = _ref8;
      return m("div", {
        className: "LrMock-row"
      }, m("span", null, label), m("span", {
        className: "LrMock-pct"
      }, when));
    }));
  }
}
flarum.reg.add('linkrobins-lr-monitoring', 'components/UptimeLanding', UptimeLanding);